import { hexToBinary } from '@streamr/utils';
import { EventEmitter } from 'eventemitter3';
import { StreamrTvBroadcaster } from '../../engine/StreamrTvBroadcaster';
import { assert } from '../../utils/assert';
import { MediaService } from '../media/MediaService';
import { ViewerCountService } from '../viewercount/ViewerCountService';
import { BroadcastServiceOutput } from './BroadcastServiceOutput';
export class BroadcastService extends EventEmitter {
    static instance;
    static getInstance() {
        if (!BroadcastService.instance) {
            BroadcastService.instance = new BroadcastService();
        }
        return BroadcastService.instance;
    }
    static getNotificationInterface() {
        return BroadcastService.getInstance();
    }
    static getEmitterInterface() {
        return BroadcastService.getInstance();
    }
    static getOutput() {
        return BroadcastServiceOutput.getReactiveOutput();
    }
    // ------------- Singleton implementation end
    mediaService;
    streamrTvBroadcaster;
    broadcastStarted = false;
    broadcastActivated = false;
    currentStreamId;
    // Output state to be used by the React components
    output;
    constructor() {
        super();
        this.output = new BroadcastServiceOutput();
        this.mediaService = MediaService.getInstance();
        this.mediaService.on('mediaStreamChanged', this.onMediaStreamChanged);
    }
    async init() {
        // ToDo: Implement initialization
    }
    onViewerCountChanged = (count) => {
        ViewerCountService.getInstance().notifyViewerCountChangedForActiveStream(count);
    };
    onMediaStreamChanged = async () => {
        if (this.broadcastStarted) {
            assert(this.streamrTvBroadcaster, 'StreamrTvBroadcaster not initialized');
            await this.streamrTvBroadcaster.stopMediaProducer();
            const mediaStream = this.mediaService.getOutputMediaStream();
            assert(mediaStream, 'Media stream is missing');
            await this.streamrTvBroadcaster.startMediaProducer(mediaStream);
        }
    };
    createStreamrTvBroadcaster(broadcast) {
        const streamrTvBroadcaster = new StreamrTvBroadcaster({
            streamId: broadcast.streamId,
            myPrivateKey: hexToBinary(broadcast.broadcasterPrivateKey),
            aesKey: hexToBinary(broadcast.aesKey),
        });
        streamrTvBroadcaster.on('audioVolume', (volume) => {
            this.output.setVolume(volume);
        });
        streamrTvBroadcaster.on('viewerCountChanged', this.onViewerCountChanged);
        streamrTvBroadcaster.on('heartbeatFromRecorder', () => {
            this.emit('heartbeatFromRecorder');
        });
        return streamrTvBroadcaster;
    }
    activateBroadcast(broadcast) {
        if (this.broadcastActivated &&
            this.currentStreamId === broadcast.streamId) {
            return;
        }
        // Stop previous broadcaster if any
        this.streamrTvBroadcaster?.stop().catch(() => undefined);
        this.streamrTvBroadcaster = this.createStreamrTvBroadcaster(broadcast);
        this.broadcastActivated = true;
        this.currentStreamId = broadcast.streamId;
        this.streamrTvBroadcaster
            .start()
            .then(() => {
            this.output.setServiceRunning(true);
        })
            .catch((error) => {
            this.output.setServiceRunning(false);
            this.broadcastActivated = false;
            console.error('Error starting broadcast service', error);
        });
    }
    async startBroadcast() {
        const mediaStream = this.mediaService.getOutputMediaStream();
        assert(mediaStream, 'Media stream is missing');
        assert(this.streamrTvBroadcaster, 'StreamrTvBroadcaster not initialized');
        await this.streamrTvBroadcaster.startMediaProducer(mediaStream);
    }
    // Notifications to be called by React components
    notifyActivateBroadcast(broadcast) {
        this.activateBroadcast(broadcast);
    }
    notifyDispose() {
        this.broadcastActivated = false;
        this.streamrTvBroadcaster?.stop().catch(() => undefined);
        this.mediaService.off('mediaStreamChanged', this.onMediaStreamChanged);
        this.output.setServiceRunning(false);
        this.broadcastStarted = false;
        this.output.setBroadcasting(false);
    }
    notifyStartBroadcast() {
        this.startBroadcast()
            .then(() => {
            this.broadcastStarted = true;
            this.output.setBroadcasting(true);
        })
            .catch((error) => {
            console.error('Error starting broadcast', error);
        });
    }
    notifyStopBroadcast() {
        this.streamrTvBroadcaster
            ?.stopMediaProducer()
            .then(() => {
            this.broadcastStarted = false;
            this.output.setBroadcasting(false);
            this.streamrTvBroadcaster?.sendLeave();
        })
            .catch((error) => {
            console.error('Error stopping broadcast', error);
        });
    }
    notifyVideoQualityChanged(quality) {
        this.streamrTvBroadcaster?.setVideoEncodingQuality(quality);
        this.output.setQuality(quality);
    }
}
